import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useActionData } from '@bonusly/common-action-input';
import {
	Box,
	Button,
	ChevronDownOutline,
	ChevronUpOutline,
	HStack,
	Image,
	Link,
	ListItem,
	Progress,
	rem,
	Spacer,
	StyleProps,
	Text,
	UnorderedList,
} from '@bonusly/design-system';
import { useAnalytics } from '@bonusly/who-am-i';

import { AmplitudeEvent } from '../../events';
import { Drink, getMeaningfulMeterImage } from '../../utils/meaningful-meter-image';

const DRINKS = [Drink.Juice, Drink.Tea, Drink.Coffee, Drink.BubbleTea, Drink.Cocoa];

export const MeaningfulMeter: FC<StyleProps> = (props) => {
	const { track } = useAnalytics();
	const { t } = useTranslation('recognition', {
		keyPrefix: 'meaningful_meter',
	});
	const [isOpen, setIsOpen] = useState(false);
	const [drink, setDrink] = useState<Drink>(
		// NOTE: select random drink to start
		DRINKS[Math.floor(Math.random() * DRINKS.length)]
	);
	const onDrinkClick = () => {
		// get the current index of the drink
		// calculate the index of the next drink by adding 1
		// use module of the length of drinks to get 0 if it is the last one
		setDrink(DRINKS[(DRINKS.indexOf(drink) + 1) % DRINKS.length]);
	};

	const { meaningfulScore: percentage } = useActionData();
	const tipImage = getMeaningfulMeterImage(percentage, drink);

	const borderRadius = percentage > 0 && percentage < 100 ? 100 : 0;

	return (
		<>
			<Box bg="blue.100">
				<Progress
					value={percentage}
					data-testid="progress-bar"
					data-progress={percentage}
					height={2}
					styleConfig={{
						baseStyle: {
							filledTrack: {
								backgroundColor: 'blue.700',
								borderTopRightRadius: borderRadius,
								borderBottomRightRadius: borderRadius,
							},
						},
					}}
				/>
			</Box>
			<Box bg="blue.100" px={6} py={2} {...props}>
				<HStack w="full" gap={rem(8)}>
					<Image
						src={tipImage}
						onClick={onDrinkClick}
						boxSize={10}
						data-testid="drink-image"
						cursor="pointer"
						_active={{ animation: 'dissolve 4s linear forwards' }}
					/>
					<Text cursor="default" fontSize={50} fontWeight="medium" lineHeight={5}>
						{t(`tip_${percentage}`)}
					</Text>
					<Spacer />
					<Button
						colorScheme="secondary"
						data-testid="toggle-button"
						onClick={() => {
							if (!isOpen) {
								track(AmplitudeEvent.MeaningfulMeterTips);
							}
							setIsOpen(!isOpen);
						}}
						right={-4}
						p={0}
						variant="ghost"
						_active={{ bg: 'transparent' }}
						_hover={{ bg: 'transparent' }}
					>
						{isOpen ? <ChevronUpOutline boxSize={6} /> : <ChevronDownOutline boxSize={6} />}
					</Button>
				</HStack>

				{isOpen && (
					<Box px={0} pt={2}>
						<Text fontSize={50} fontWeight="medium">
							{t('header')}:
						</Text>
						<UnorderedList pl={2}>
							{[0, 20, 40, 60, 80].map((amount) => (
								<ListItem key={amount}>
									<Text display="inline" fontSize={50} fontWeight="semibold" pr={1}>
										{t(`tip_${amount}`)}:
									</Text>
									<Text display="inline" fontSize={50}>
										{t(`description_${amount}`)}
									</Text>
								</ListItem>
							))}
						</UnorderedList>
						<Text fontSize={50} fontWeight="medium" lineHeight={5} display="inline" pr={1}>
							{t('more_tips')}
						</Text>
						<Link
							href="https://bonusly.com/post/a-guide-to-giving-employee-recognition-like-a-pro"
							target="_blank"
							color="blue.500"
							display="inline"
							fontSize={50}
							fontWeight="medium"
							lineHeight={5}
						>
							{t('here')}
						</Link>
					</Box>
				)}
			</Box>
		</>
	);
};
